import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Grid, Menu } from "antd";
import Icon from "../util-components/Icon";
import navigationConfig from "configs/NavigationConfig";
import { SIDE_NAV_LIGHT } from "constants/ThemeConstant";
import utils from "utils";
import { useTheme } from "../../data/hooks/theme/useTheme";
import roles from "roles";
import { ExclamationCircleFilled, LogoutOutlined, SettingOutlined, UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { doc, getDoc } from "firebase/firestore";
import { firestore } from "../../configs/FirebaseConfig";
import i18n from "translations/i18n";
import { FIRESTORE_USER_PERMISSIONS_TABLE } from "../../constants/FirestoreConstant";
import urls from "urls";
import styled from "styled-components";
import useIsMobile from "../../hooks/useIsMobile";
import { useCustomers } from "../../data/hooks/firestore/useCustomers";
import { useAuth } from "../../data/hooks/firebase/useAuth";

const { useBreakpoint } = Grid;

const setDefaultOpen = (key) => {
  let keyList = [];
  let keyString = "";
  if (key) {
    const arr = key.split("-");
    for (let index = 0; index < arr.length; index++) {
      const elm = arr[index];
      index === 0 ? (keyString = elm) : (keyString = `${keyString}-${elm}`);
      keyList.push(keyString);
    }
  }
  return keyList;
};

const SideNavContent = (props) => {
  const { t } = useTranslation();
  const { routeInfo, hideGroupTitle } = props;
  const { sideNavTheme, navCollapsed, toggleMobileNav } = useTheme();
  const isMobile = !utils.getBreakPoint(useBreakpoint()).includes("lg");
  const isMobileHook = useIsMobile();

  // Filter navigation items based on mobile/desktop - moved inside component
  const filterNavigationByDevice = (navigationItems) => {
    return navigationItems.map((item) => ({
      ...item,
      submenu: item.submenu
        ? item.submenu
            .filter((subItem) => {
              // Hide mobile-only items on desktop
              if (subItem.mobileOnly && !isMobileHook) {
                return false;
              }
              if (subItem.onlyTest && process.env.REACT_APP_ENVIRONMENT === "production") {
                return false;
              }
              return true;
            })
            .map((subItem) => ({
              ...subItem,
              submenu: subItem.submenu
                ? subItem.submenu.filter((subSubItem) => {
                    if (subSubItem.mobileOnly && !isMobileHook) {
                      return false;
                    }
                    return true;
                  })
                : [],
            }))
        : [],
    }));
  };

  const filteredNavigation = filterNavigationByDevice(navigationConfig);

  const closeMobileNav = () => {
    if (isMobile) {
      toggleMobileNav(false);
    }
  };

  const { role, uid: userId, email: authEmail, signOut } = useAuth();
  const { newCustomers, isLoading: customersLoading } = useCustomers();

  const [userPermissions, setUserPermissions] = useState({});
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  useEffect(() => {
    const loadUserPermissions = async () => {
      // If not admin, no need to load permissions
      if (!role || role !== roles.admin) {
        setPermissionsLoaded(true);
        return;
      }

      // Only load permissions for admin users
      if (role === roles.admin) {
        try {
          const userPermissionsDocRef = doc(firestore, FIRESTORE_USER_PERMISSIONS_TABLE, userId);
          const userPermissionsDoc = await getDoc(userPermissionsDocRef);
          if (userPermissionsDoc.exists()) {
            setUserPermissions(userPermissionsDoc.data());
          }
          setPermissionsLoaded(true);
        } catch (error) {
          console.error("Error loading user permissions:", error);
          setPermissionsLoaded(true);
        }
      } else {
        setPermissionsLoaded(true);
      }
    };

    loadUserPermissions();
  }, [role, userId, firestore]);

  const hasAccess = (menu) => {
    // If permissions not loaded yet, don't show menu items
    if (!permissionsLoaded) return false;

    // If role not loaded yet, no access
    if (!role) return false;

    // Special case: reconciliation - only iwan@digitalkiss.nl
    if (menu.key === "shop-products-reconciliation") {
      return authEmail === "iwan@digitalkiss.nl";
    }

    // SuperAdmin has access to everything
    if (role === roles.superAdmin) return true;

    // Check if it's a superAdmin-only item
    if (menu.isAdmin === false && role !== roles.superAdmin) return false;

    // For admin users, check user-specific permissions
    if (role === roles.admin) {
      // For menu items with submenu's, check if any child has access
      if (menu.submenu && menu.submenu.length > 0) {
        return menu.submenu.some((subMenu) => {
          // Check if the submenu itself has access
          if (userPermissions[subMenu.key]?.view === true) return true;

          // If the submenu has child items, check those
          if (subMenu.submenu && subMenu.submenu.length > 0) {
            return subMenu.submenu.some((subSubMenu) => userPermissions[subSubMenu.key]?.view === true);
          }
          return false;
        });
      }
      // For specific menu items, check direct permission
      return userPermissions[menu.key]?.view === true;
    }

    // Legacy fallback for other roles
    if (menu.isAdmin && role === roles.admin) return true;
    if (!menu.isAdmin) return true;

    return false;
  };

  const hasNewCustomers = !customersLoading && newCustomers && newCustomers.length > 0;

  const handleSignOut = (e) => {
    e.preventDefault();
    signOut();
  };

  const changeLanguage = (language) => {
    i18n.changeLanguage(language);
  };

  // Convert navigation to Antd v5 menu items format
  const convertToMenuItems = (navigationItems) => {
    return navigationItems
      .filter((menu) => hasAccess(menu))
      .map((menu) => {
        if (menu.submenu.length > 0) {
          // Group with submenu items
          const children = menu.submenu
            .filter((subMenuFirst) => hasAccess(subMenuFirst))
            .filter((subMenuFirst) => !subMenuFirst.hidden)
            .map((subMenuFirst) => {
              if (subMenuFirst.submenu.length > 0) {
                // Nested submenu
                return {
                  key: subMenuFirst.key,
                  icon: subMenuFirst.icon ? <Icon type={subMenuFirst?.icon} /> : null,
                  label: t(subMenuFirst.title),
                  children: subMenuFirst.submenu
                    .filter((subMenuSecond) => hasAccess(subMenuSecond))
                    .filter((subMenuSecond) => !subMenuSecond.hidden)
                    .map((subMenuSecond) => ({
                      key: subMenuSecond.key,
                      icon: subMenuSecond.icon ? <Icon type={subMenuSecond?.icon} /> : null,
                      label: (
                        <Link onClick={() => closeMobileNav()} to={subMenuSecond.path}>
                          {t(subMenuSecond.title)}{" "}
                          {subMenuSecond.title === "customers_pending" && hasNewCustomers && (
                            <ExclamationCircleFilled style={{ fontSize: "12px", color: "#3e82f7" }} />
                          )}
                        </Link>
                      ),
                    })),
                };
              } else {
                // Single menu item
                return {
                  key: subMenuFirst.key,
                  icon: subMenuFirst.icon ? <Icon type={subMenuFirst.icon} /> : null,
                  label: (
                    <Link onClick={() => closeMobileNav()} to={subMenuFirst.path}>
                      {t(subMenuFirst.title)}
                    </Link>
                  ),
                };
              }
            });

          return {
            key: menu.key,
            label: t(menu.title),
            type: "group",
            children: children,
          };
        } else {
          // Single menu item without submenu
          return {
            key: menu.key,
            icon: menu.icon ? <Icon type={menu?.icon} /> : null,
            label: menu.path ? (
              <Link onClick={() => closeMobileNav()} to={menu.path}>
                {t(menu?.title)}
              </Link>
            ) : (
              t(menu?.title)
            ),
          };
        }
      });
  };

  const menuItems = convertToMenuItems(filteredNavigation);

  return (
    <div
      style={{
        height: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          overflowX: "hidden",
          justifyContent: "space-between",
          height: "100%",
        }}
      >
        <Menu
          theme={sideNavTheme === SIDE_NAV_LIGHT ? "light" : "dark"}
          mode="inline"
          style={{ borderRight: 0 }}
          defaultSelectedKeys={[routeInfo?.key]}
          defaultOpenKeys={setDefaultOpen(routeInfo?.key)}
          className={hideGroupTitle ? "hide-group-title" : ""}
          items={menuItems}
        />
      </div>
      {/* Bottom section with user controls - only show when not collapsed */}
      {!navCollapsed && (
        <LowerMenu
          style={{
            borderTop: sideNavTheme === SIDE_NAV_LIGHT ? "1px solid #f0f0f0" : "1px solid #303030",
            backgroundColor: sideNavTheme === SIDE_NAV_LIGHT ? "#f9f9f9" : "#001529",
            marginBottom: 0,
          }}
        >
          {/* Language selector */}
          {/*<div style={{ marginBottom: "12px" }}>*/}
          {/*  <Radio.Group*/}
          {/*    options={[*/}
          {/*      { label: "English", value: "en" },*/}
          {/*      { label: "Nederlands", value: "nl" },*/}
          {/*    ]}*/}
          {/*    onChange={(e) => {*/}
          {/*      console.log(e);*/}
          {/*      changeLanguage(e.target.value);*/}
          {/*    }}*/}
          {/*    value={i18n.language}*/}
          {/*    optionType="button"*/}
          {/*    buttonStyle="solid"*/}
          {/*    size="small"*/}
          {/*  />*/}
          {/*</div>*/}

          <LowerMenuItem style={{ cursor: "default" }}>
            <UserOutlined /> <span style={{ marginLeft: "3px" }}>{authEmail}</span>
          </LowerMenuItem>

          <LowerMenuItem>
            <Link
              onClick={() => closeMobileNav()}
              to={urls.profile}
              style={{ color: "inherit", textDecoration: "none" }}
            >
              <SettingOutlined /> <span style={{ marginLeft: "3px" }}>{t("profile_settings")}</span>
            </Link>
          </LowerMenuItem>

          <LowerMenuItem onClick={(e) => handleSignOut(e)}>
            <LogoutOutlined /> <span style={{ marginLeft: "3px" }}>{t("logout")}</span>
          </LowerMenuItem>
        </LowerMenu>
      )}
    </div>
  );
};

const LowerMenu = styled.ul`
  flex-shrink: 0;
  padding: 16px;
  list-style: none;
`;

const LowerMenuItem = styled.li`
  margin-bottom: 12px;
  transition: color 0.2s ease;

  &:not(:first-child):hover {
    cursor: pointer;
    color: rgb(62, 121, 247);
  }
`;

const MenuContent = (props) => {
  return <SideNavContent {...props} />;
};

export default MenuContent;
